import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';

/**
 * Express Market — client-only cart.
 * Persisted in localStorage under 'em.cart.v1.<email>' so different users
 * on the same browser each have an independent cart.
 */
const CartContext = createContext(null);

const storageKey = (email) => email ? `em.cart.v1.${email}` : 'em.cart.v1';

const readStorage = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeStorage = (key, items) => {
  try { localStorage.setItem(key, JSON.stringify(items)); } catch (_) { /* ignore */ }
};

export function ExpressMarketCartProvider({ children }) {
  const { user } = useAuth();
  const key = storageKey(user?.email);

  const [items, setItems] = useState(() => readStorage(storageKey(
    (() => { try { const u = localStorage.getItem('user'); return u ? JSON.parse(u)?.email : null; } catch { return null; } })()
  )));

  // When the active user/key changes, reload from the correct storage bucket.
  useEffect(() => {
    setItems(readStorage(key));
  }, [key]);

  useEffect(() => { writeStorage(key, items); }, [key, items]);

  const addItem = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const minQty = Number(product.min_order_qty) > 0 ? Number(product.min_order_qty) : 1;
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        // Quick-add raises the line to at least the product minimum so the
        // order never fails server-side on minimum_order_quantity.
        const next = Number(existing.qty) + (Number(qty) > 0 ? Number(qty) : minQty);
        return prev.map((i) =>
          i.product_id === product.id ? { ...i, qty: Math.max(minQty, next) } : i
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          sku: product.sku,
          name: product.name,
          slug: product.slug,
          image_url: product.image_url,
          unit_price: Number(product.list_price) || 0,
          currency_code: product.currency_code || 'KES',
          uom: product.uom || 'EA',
          min_order_qty: minQty,
          supplier_display_name: product.supplier_display_name,
          qty: Math.max(minQty, Number(qty) > 0 ? Number(qty) : minQty),
        },
      ];
    });
  }, []);

  const updateQty = useCallback((product_id, qty) => {
    setItems((prev) => prev.map((i) => {
      if (i.product_id !== product_id) return i;
      const minQty = Number(i.min_order_qty) > 0 ? Number(i.min_order_qty) : 1;
      return { ...i, qty: Math.max(minQty, Number(qty) || minQty) };
    }));
  }, []);

  const removeItem = useCallback((product_id) => {
    setItems((prev) => prev.filter((i) => i.product_id !== product_id));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + Number(i.unit_price) * Number(i.qty), 0),
    [items]
  );
  const itemCount = useMemo(() => items.reduce((s, i) => s + Number(i.qty), 0), [items]);

  const value = useMemo(() => ({
    items, addItem, updateQty, removeItem, clearCart, subtotal, itemCount,
  }), [items, addItem, updateQty, removeItem, clearCart, subtotal, itemCount]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useExpressMarketCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) {
    // Fallback no-op to avoid crashes when used outside provider.
    return {
      items: [], addItem: () => {}, updateQty: () => {}, removeItem: () => {},
      clearCart: () => {}, subtotal: 0, itemCount: 0,
    };
  }
  return ctx;
};

export default ExpressMarketCartProvider;
